import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import classNames from 'classnames';
import styles from './HistoricRiskDataGrid.module.scss';
import { TableHeader } from '../TableHeader';
import { Loading } from '../Loading';
import { isEmpty, isEmptyArray, numberWithCommas, formatPercent, getQueryString } from '../../util/functions';
import { getRiskData, downloadRiskData } from '../../api/riskDashboard';
import DataGrid, { Column, Scrolling, Summary, TotalItem, ColumnFixing, Export } from 'devextreme-react/data-grid';
import { COLUMN_HEIGHT } from '../../util/dashBoardPageConstants';


var noSort = {
  mode: 'none'
};
var noPaging = {
  enabled: false
};

var loadingOptions = {
  enabled: false
};

const formatNumber = {
  type: 'fixedPoint',
  precision: 0
};

const getFilterObj = (filterObj = {}) => {
  let filter = {};
  if (filterObj) {
    const systemAcked = filterObj?.systemAcked;
    if (null != systemAcked) {
      filter.systemAcked =systemAcked;
    }
    if (!isEmpty(filterObj.mpid)) {
      filter.mpid = filterObj.mpid;
    }
    if (!isEmpty(filterObj.date)) {
      filter.date = filterObj.date;
    }
  }
  return filter;
};


const customizeBoolean = (cellInfo) => {
  return cellInfo.value ? 'Yes' : 'No';
};

const addPercentToData = (data) => {
  if (undefined !== data.value) {
    return formatPercent(data.value);
  }
  return data.value;
};

const isFilterNotEqual = (oldFilter = {}, newFilter = {}) => {
  if (oldFilter.date !== newFilter.date) {
    return true;
  } else if (oldFilter.mpid !== newFilter.mpid) {
    return true;
  } else if (oldFilter.systemAcked !== newFilter.systemAcked) {
    return true;
  }
  return false;

}


class HistoricRiskDataGrid extends React.Component {
  constructor(props) {
    super(props);
    this.dataGridRef = React.createRef();
    this.state = {
      data: undefined,
      isLoaded: false,
      isDownloadInactive: false
    };
    this.populateGridData = this.populateGridData.bind(this);
    this.getGridDataAPI = this.getGridDataAPI.bind(this);
    this.download = this.download.bind(this);
    this.renderColumns = this.renderColumns.bind(this);
    this.handleErrorResults = this.handleErrorResults.bind(this);
    this.renderSummary = this.renderSummary.bind(this);
    this.customizeTotal = this.customizeTotal.bind(this);


  }

  get dataGrid() {
    var gridRef;
    if (this.dataGridRef && this.dataGridRef.current) {
      gridRef = this.dataGridRef.current.instance;
    }
    return gridRef;
  }

  componentDidMount() {
    // try to get latest data from API
    this.getGridDataAPI();
  }

  
  componentDidUpdate (prevProps) {
    const filter = this.props.filter;
    if (filter && isFilterNotEqual(prevProps.filter, filter)) {
        this.setState(prevState => ({
          isLoaded: false
        }), this.getGridDataAPI);
      }
  }


  getGridDataAPI() {
    let api = this.props.api;
    const filter = getFilterObj(this.props.filter);
    const filterQuery = getQueryString(filter);
    if (!isEmpty(api)) {
      api = `${api}?${filterQuery}`;
      getRiskData(api)
        .then(data => {
            this.populateGridData(data);
        })
        .catch((error) => {
          const message = (error && error.response &&
            error.response.data && error.response.data) || 'Data currently unavailable';
          this.handleErrorResults(message);
        });
    }
  }

  download () {
    if (this.state.isDownloadInactive) return;
    // make it inactive for future request until current one is solved
    this.setState({ isDownloadInactive: true });
    // download  data
    let api = this.props.downloadApi;
    if (!isEmpty(api)) {
      const header = this.props.identifier && this.props.identifier.toUpperCase().replaceAll('-', '_');
      const historicDate = this.props.filter?.date;;
      const name = `${header}-${historicDate}`;
      const filter = getFilterObj(this.props.filter);
      const filterQuery = getQueryString(filter);
      api = `${api}?${filterQuery}`;
      downloadRiskData(api, name).then(data => {
        this.setState({ isDownloadInactive: false });
      })
        .catch((err) => {
        // error
          this.setState({ isDownloadInactive: false });
          console.log(err);
        });
    } else {
      if (this.dataGrid) {
        this.dataGrid.exportToExcel(false);
      }
      this.setState({ isDownloadInactive: false });
    }
  }

  populateGridData(data) {
    const totalField = this.props.totalField;
    let list = data.alerts;
    if (isEmptyArray(list)) {
      // console.log('No Data Found');
      list = [];
    }
    const totalCount = (data && data[totalField] && numberWithCommas(data[totalField])) || '';
    this.setState({
      data: list,
      totalCount: totalCount,
      isLoaded: true
    });
  }

  handleErrorResults(errorMessage) {
    this.setState(prevState => ({
      isLoaded: true,
      data: {},
      error: errorMessage
    }));
  }


  renderColumns() {
    const _this = this;
    return this.props.columns && this.props.columns.map(function (item, index) {
      if (item.type === 'number') {
        return <Column height={COLUMN_HEIGHT} format={formatNumber} type='number' key={index + item.caption} dataField={item.dataField} caption={item.caption} />;
      } else if (item.type === 'percent') {
        return <Column height={COLUMN_HEIGHT} customizeText={addPercentToData} key={index + item.caption} dataField={item.dataField} caption={item.caption} />;
      }  else if(item.type === 'boolean') {
        return <Column height={COLUMN_HEIGHT} dataType='string' customizeText={customizeBoolean} key={index + item.caption} dataField={item.dataField} caption={item.caption} />;
      } else {
        return <Column height={COLUMN_HEIGHT} key={index + item.caption} dataField={item.dataField} caption={item.caption} />;
      }
    });
  }

  renderSummary () {
    const summaryGroupTitleColumn = this.props.summaryGroupTitleColumn;
    const summaryGroupDataColumn = this.props.summaryGroupDataColumn;
    const totalField = this.props.totalField;
    if (summaryGroupTitleColumn && summaryGroupDataColumn && totalField) {
      return <Summary >
        <TotalItem
          column={summaryGroupTitleColumn.dataField}
          summaryType='count'
          customizeText={this.customizeSummaryTitle} />
        <TotalItem
          column={summaryGroupDataColumn.dataField}
          summaryType='count'
          cssClass={'dashboardGridSummaryTotalNumber'}
          customizeText={this.customizeTotal} />
      </Summary>;
    } else {
      return <div />;
    }
  }

  customizeTotal (data) {
    return `${this.state.totalCount}`;
  }

  customizeSummaryTitle () {
    return `Total`;
  }

  render() {
    const header = this.props.header;
    const historicDate = this.props.filter?.date;;
    const downloadHeader = this.props.identifier && this.props.identifier.toUpperCase().replaceAll('-', '_');
    const downloadFilename = `${downloadHeader}-${historicDate}`;
    const data = this.state.data;

    return (
      <div>
        {historicDate &&
      <div className={classNames(styles.container)}>
        <TableHeader headerUpperCase={false}
          header={header}
          className={styles.tableHeader}
          download={this.download}
          downloadIconId={this.props.identifier.concat('-download')}
          tableHeaderMargin={false}
          topBorderOnly={false}
          bottomBorderOnly />
        <Loading
          type='table'
          data={data}
          loaded={this.state.isLoaded}
          reloading={false}
          error={this.state.error}
          emptyMessage='No results found.' >
          <DataGrid
            ref={this.dataGridRef}
            id={this.props.identifier}
            dataSource={this.state.data}
            columnAutoWidth
            showColumnLines
            showRowLines
            rowAlternationEnabled
            showBorders
            repaintChangesOnly
            paging={noPaging}
            sorting={noSort}
            loadPanel={loadingOptions}
            height={this.props.maxRows ? (this.props.maxRows + 1) * COLUMN_HEIGHT : 450}
            cacheEnabled={false}
          >
            {this.renderColumns()}

            <Scrolling mode='virtual' />
            <Export enabled fileName={downloadFilename} />
            {this.renderSummary()}

            <ColumnFixing enabled />
          </DataGrid>
        
        </Loading>
      </div> }

      </div>
);
}
}

HistoricRiskDataGrid.propTypes = {
  api: PropTypes.string.isRequired,
  downloadApi: PropTypes.string,
  header: PropTypes.string.isRequired,
  columns: PropTypes.array.isRequired,
  lastUpdatedField: PropTypes.string.isRequired,
  totalField: PropTypes.string,
  identifier: PropTypes.string.isRequired,
  maxRows: PropTypes.number
};

export default HistoricRiskDataGrid;
